import {settleExistingSection} from '../../core/actionHighlighter';
import {dispatchAcknowledgedEvent} from '../../core/resourceEvents';
import {ROLE_FILTER_EVENT, ROLE_FOCUS_EVENT} from './events';
import {categories as roleCategories} from '../../../pages/systemSettings/userManagement/roles/roleAdd/roleAddConstants';

// Built-in roles that can't be edited or deleted, from any entry point
// (manual row actions, bulk actions, or agentBridge/HFCL chat commands).
const LOCKED_ROLE_NAMES = ['Viewer', 'Operator'];

/**
 * Whether a role is a built-in one with no edit/delete actions available.
 * @param {object} row Canvas role row
 * @return {boolean} true when locked
 */
export function isRoleLocked(row) {
	return LOCKED_ROLE_NAMES.includes(row?.name);
}

// "Privileged" is accepted as an alias for the stored value "Previleged",
// since that's the label shown in the UI.
const ROLE_CATEGORY_LABEL_ALIASES = {previleged: 'Privileged'};

/**
 * Resolve a chat-supplied category name (e.g. "Read Only", "Privileged") to
 * the exact value RoleAdd's category select expects, matching against the
 * same list the dropdown itself offers.
 * @param {string} raw category name from chat initial values
 * @return {string|undefined} matching category value, or undefined if invalid
 */
export function resolveRoleCategory(raw) {
	if (!raw) return undefined;
	const normalized = String(raw).trim().toLowerCase();
	const match = roleCategories.find((category) => {
		const label = (ROLE_CATEGORY_LABEL_ALIASES[category.value.toLowerCase()] || category.label).toLowerCase();
		return category.value.toLowerCase() === normalized || label === normalized;
	});
	return match?.value;
}

/**
 * Open the Roles list on Canvas Settings.
 *
 * Only the tab is closed - the Roles table is rendered with the Users one
 * and needs nothing opened. The resource is user_role to keep "role" free
 * for the Access Point and Site setting of that name; the table on screen
 * is still called Roles.
 *
 * @return {Promise<void>} completion
 */
export async function openRolesSection() {
	// One level, not two. User Management renders the Roles and Users
	// tables together with plain text headings - checked in the rendered
	// page, where the tab has no accordions at all and no control answers
	// to "Roles". Selecting the tab is the whole of opening this section.
	//
	// Settled rather than clicked once, for the same reason as Users: the
	// tabs are often not in the DOM yet when this runs.
	try {
		return await settleExistingSection('User Management');
	} catch (_) {
		throw new Error('user_role_section_unavailable');
	}
}

/**
 * Filter the Roles table to a search term.
 * @param {string} query search text
 * @return {Promise<void>} acknowledgement
 */
export function applyRoleFilter(query) {
	return dispatchAcknowledgedEvent(ROLE_FILTER_EVENT, {
		query: String(query || '').trim(),
		resource: 'role',
	});
}

/**
 * Point at one role's row in the table.
 * @param {string} entityId role identifier
 * @return {Promise<void>} acknowledgement
 */
export function focusRole(entityId) {
	if (!entityId) return Promise.reject(new Error('invalid_role_focus'));
	return dispatchAcknowledgedEvent(ROLE_FOCUS_EVENT, {
		entityId: String(entityId),
		resource: 'role',
	});
}
